/**
 * The `Line` catalog component: series, markers, a legend, a caption.
 *
 * An A2UI implementation. `LineApi` is the client half of the entry that
 * artifacts/a2ui-catalog-v1.json carries for "Line". Every prop is a
 * `{path}` binding and the schema says so: `PathBinding` is strict, so a
 * literal list, a `{call}` function binding or an extra key fails the
 * processor's validation before any state changes, and `Surface` draws
 * nothing. The protocol's `DynamicValue` would admit all three — the
 * reviewer of slice 0 showed a `{call}` arriving inert only because the
 * function map was empty — and "a binding is a lookup, never a transform"
 * is worth more as a refusal than as a description.
 *
 * Drawn from the data model the server composed — nothing here computes a
 * value, and every string printed is the wire's own. The geometry is
 * `plot()` from lib/series.ts, the same rule the legacy `series` shape uses:
 * scaled to its own values, never to zero, because a zero baseline draws a
 * week of tightly clustered NEPSE closes as a flat line, which is a false
 * statement about volatility made by the axis.
 *
 * A marker is a vertical rule at an `x` the series carries. The server
 * already refused any marker at an `x` no point carries, so a marker that
 * arrives here always has a point to stand on; if the label is not among the
 * plotted points (a series the client dropped as unplottable) it is skipped
 * rather than drawn at zero.
 */
import { z } from 'zod/v3'
import { CommonSchemas, type ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import type { CellValue } from '@/features/mitra/lib/figures'
import type { LineStyle, SeriesRole } from '@/features/mitra/api/frames'
import { PLOT_H, PLOT_W, plot, type SeriesLine } from '@/features/mitra/lib/series'
import { nptDate } from '@/features/mitra/lib/npt'
import { Label } from '@/features/mitra/panel/blocks/Card'

/**
 * `CommonSchemas.DataBinding` is `@a2ui/web_core`'s OWN private zod@3.25.76
 * (a regular, non-peer dependency — it never sees mm-web's zod). This file's
 * `z` is zod v4's bundled `zod/v3` compatibility shim, a separate
 * reimplementation of the same v3 shape, needed because the schemas below use
 * v3-era zod calls the ported code was written against. Unioning a value
 * typed by one deeply-recursive generic `ZodType` hierarchy with a value from
 * the other sends tsc into unbounded structural comparison: the two are close
 * enough to keep recursing, never identical enough to short-circuit.
 * Confirmed in isolation — a four-line file doing just this union OOMs tsc at
 * any heap size, and stops OOMing the moment the cast below is in place.
 * The cast crosses the type boundary once, here, and changes nothing at
 * runtime: it is still the exact same zod3 schema object underneath.
 */
const DataBinding = CommonSchemas.DataBinding as unknown as z.ZodTypeAny

/**
 * A lookup into the surface's data model, and nothing else.
 *
 * A union, because the renderer's binder resolves a prop only when its
 * schema is a union with a `{path}` arm (a bare `DataBinding` object is
 * classed static and arrives unresolved — measured, not read). The other
 * arm is `never`, so the union admits exactly one shape: what this drops
 * from the protocol's `DynamicValue` is the function call and the
 * literal, both of which now fail validation before any state changes.
 */
export const PathBinding = z.union([DataBinding, z.never()])

export const LineApi = {
  name: 'Line',
  schema: z
    .object({
      series: PathBinding,
      markers: PathBinding.optional(),
      legend: PathBinding.optional(),
      caption: PathBinding.optional(),
    })
    .strict(),
}

/**
 * The shapes behind the four paths, fixed by `to_messages`. Every value
 * position is a `_measure`d figure, which `CellValue` covers.
 */
export type SeriesPart = {
  id: string
  style: LineStyle
  role: SeriesRole
  points: { x: string; y: CellValue }[]
}
export type MarkerPart = { id: string; at: string; label: string }
export type LegendPart = { id: string; text: string }

/**
 * A bound list, or nothing. A path the data model does not carry resolves
 * to undefined rather than throwing, and a chart with no markers is a
 * chart, not an error.
 */
function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once more — see the
// comment on `PathBinding` above for why the union crashes tsc. This call
// site has its OWN version of the same crash, and it is `pnpm lint`'s to
// have, not `pnpm typecheck`'s: eslint's type-aware rules re-walk an inline
// `LineApi as unknown as ComponentApi` argument and re-derive the same
// runaway comparison at every `createComponentImplementation` call site —
// confirmed by bisecting a twelve-file repro down to this exact shape, where
// pulling the cast onto its own line (never inlining it as an argument) is
// what stopped the crash. Named here so the cast is fully resolved BEFORE it
// is passed in; the value crossing the boundary is unchanged either way.
const lineApi = LineApi as unknown as ComponentApi

export const Line = createComponentImplementation<ComponentApi>(
  lineApi,
  ({
    props,
  }: {
    props: { series?: unknown; markers?: unknown; legend?: unknown; caption?: unknown }
  }) => (
    <LineFigure
      series={listOf<SeriesPart>(props.series)}
      markers={listOf<MarkerPart>(props.markers)}
      legend={listOf<LegendPart>(props.legend)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

const PAD_L = 3
const PAD_R = 6
const PAD_T = 13
const PAD_B = 13
const VIEW_W = PAD_L + PLOT_W + PAD_R
const VIEW_H = PAD_T + PLOT_H + PAD_B
/** User units, not CSS pixels: this scales with the box, and is meant to.
 *  The sibling's own axis size, because the two drawings are one language. */
const AXIS_SIZE = 8

const DASH: Record<string, string | undefined> = {
  solid: undefined,
  dashed: '6 4',
  dotted: '2 3',
}

/** The drawing, given the four lists the surface resolved. */
/** A window end as a reader reads it: a date where the wire sent one, and the
 * wire's own string where it did not. The twin of `axisLabel` in
 * `shapes/Series.tsx`; both exist so the two renderers read a window the same
 * way. */
function axisLabel(x: string | null): string {
  if (!x) return ''
  return nptDate(x) || x
}

function LineFigure({
  series,
  markers,
  legend,
  caption,
}: {
  series: SeriesPart[]
  markers: MarkerPart[]
  legend: LegendPart[]
  caption: string | null
}) {
  const lines: SeriesLine[] = series.map((s) => ({ name: s.id, points: s.points }))
  const p = plot(lines, PLOT_W, PLOT_H)
  // The unit the scale is in, once. `plot` leaves it empty unless every
  // plotted figure agrees, so this can never name a scale the drawing does
  // not have -- and the legacy `series` drawing states it the same way, which
  // is the point of both reading one `plot`.
  const unit = `${p.unit.before}${p.unit.after}`.trim()
  if (!p.plottable) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }

  // Marker x-positions come from the plotted points of the first drawable
  // line: the same coordinates the path is in, never a second scale.
  const xOf = new Map((p.lines[0]?.points ?? []).map((pt) => [pt.label, pt.x]))
  // MORE THAN ONE LINE ON THIS AXIS. Every decision that changes with it is
  // a decision about ambiguity: a reference rule that names one line while
  // two are drawn says nothing about either.
  const many = p.lines.length > 1
  // The window's ends come from the SERIES rows rather than from `p`, because
  // `plot()` returns drawing coordinates and the label a reader needs is the
  // string the wire sent. `series[0]` is the same line `xOf` keys on above.
  const pts = series[0]?.points ?? []
  const firstPt = pts[0]
  const lastPt = pts[pts.length - 1]
  const firstX = firstPt ? firstPt.x : null
  const lastX = lastPt ? lastPt.x : null

  return (
    <figure
      data-role="chart"
      data-component="line"
      className="m-0 flex flex-col gap-1 px-3 pb-2 pt-2"
    >
      {/* THE DRAWING IS NAMED ONLY WHERE THERE IS MORE THAN ONE, which is
          `shapes/Series.tsx`'s rule adopted here so the two cannot say
          different things about the same card. A single legend entry was
          drawn on every chart this component owns, so "how has NEPSE
          performed this year" answered with a line titled NEPSE INDEX VALUE
          under a sentence that had just said NEPSE -- the heading that says
          nothing, on a surface whose whole idiom is a ledger that states each
          figure once. With two lines on one axis the words are the only thing
          telling them apart, and that is the case this keeps.

          What a reader loses is a chart that names itself when it is lifted
          out of its card. The card is what carries the subject: the sentence
          above it, the entity name on the header, the freshness rail under
          it. None of those travel in a screenshot of the drawing alone. */}
      {legend.length > 1 && (
        <div data-role="legend" className="flex flex-wrap gap-x-3">
          {legend.map((l) => (
            <Label key={l.id}>{l.text}</Label>
          ))}
        </div>
      )}
      <svg
        viewBox={`0 0 ${String(VIEW_W)} ${String(VIEW_H)}`}
        preserveAspectRatio="xMidYMid meet"
        className="block h-auto w-full"
        role="img"
        aria-label={lines.map((l) => l.name).join(', ')}
      >
        {/* NO GRADIENT AND NO FILLED REGION. This block declared one and
            never referenced it, which is dead weight under every line the
            engine draws. The deck settles the question anyway: of its 159
            line charts 147 are `fill="none"`, and every one of those is a
            Mitra reply. A faded region belongs to the app's own pages. */}
        <g transform={`translate(${String(PAD_L)} ${String(PAD_T)})`}>
          {/* RULED FIRST, so the lines draw over the scale. The high and the
              low were two bare strings in two corners here and gridlines on
              the drawing in the sibling, which is the same figure said two
              ways -- and a reader cannot see which renderer answered. `plot`
              is the one source for both: `marks` names values the series
              REACHES, never round ones, and a flat series gets one mark
              because high and low are then one value. */}
          {p.marks.map((mark, index) => (
            <g key={mark.y}>
              <line
                data-role="chart-grid"
                x1={0}
                y1={mark.y}
                x2={PLOT_W}
                y2={mark.y}
                fill="none"
                className="stroke-edge"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              {mark.text && (
                <text
                  data-role="chart-axis"
                  x={0}
                  // The high above its line and the low below it, by index and
                  // not by coordinate: a flat series has one mark and it sits
                  // in the middle of the box.
                  y={index === 0 ? mark.y - 4 : mark.y + AXIS_SIZE - 1}
                  fontSize={AXIS_SIZE}
                  className="fill-ink-soft font-mono tabular-nums"
                >
                  {mark.text}
                </text>
              )}
            </g>
          ))}
          {/* WHERE THE WINDOW OPENED, which is what turns a shape into a
              comparison: the question behind "how has this done over a
              period" is whether it is above or below where it started. This
              component drew the high, the low and nothing else, so the one
              thing the question was about was the one thing absent.

              ONE LINE ONLY, and dashed in the rule's own ink, both for the
              reasons the sibling gives: a reference attached to the wrong
              series is worse than none, and a solid rule would read as a
              second series. `plot` withholds it where it lands on a mark
              already drawn. */}
          {!many && p.open && (
            <g data-role="chart-open">
              <line
                x1={0}
                y1={p.open.y}
                x2={PLOT_W}
                y2={p.open.y}
                fill="none"
                className="stroke-ink-soft"
                strokeWidth={1}
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
              {p.open.text && (
                <text
                  data-role="chart-open-label"
                  x={PLOT_W}
                  textAnchor="end"
                  y={p.open.y - 3}
                  fontSize={AXIS_SIZE}
                  className="fill-ink-soft font-mono tabular-nums"
                >
                  {p.open.text}
                </text>
              )}
            </g>
          )}
          {p.lines.map((line, i) => (
            <path
              key={line.name || i}
              data-role="series-path"
              d={line.d}
              fill="none"
              strokeWidth={series[i]?.role === 'benchmark' ? 1.4 : 1.9}
              strokeDasharray={DASH[series[i]?.style ?? 'solid']}
              // THE FIRST LINE CARRIES THE DIRECTION AND A BENCHMARK STAYS
              // QUIET. The deck strokes the reader's own series in its accent
              // and the index it is measured against in grey (13f, 20a), and
              // that contrast is the whole of how the two are told apart at a
              // glance. `--color-up` / `--color-down` rather than the deck's
              // own #00c805, which embed.css measured at 2.73:1: a colour a
              // reader cannot see is not a faithful colour.
              //
              // The quiet half is the TAIL'S ink and not the body-text grey,
              // for the reason `shapes/Series.tsx` gives at `strokeOf`: in
              // dark, --color-ink-soft and --color-up sit at the same OKLCH
              // lightness, so the line that is meant to recede does not.
              className={
                series[i]?.role === 'benchmark' || i > 0
                  ? 'stroke-series-rest-ink'
                  : p.direction === 'up'
                    ? 'stroke-up'
                    : p.direction === 'down'
                      ? 'stroke-down'
                      : 'stroke-ink'
              }
            />
          ))}
          {markers.map((m) => {
            const x = xOf.get(m.at)
            if (x === undefined) return null
            return (
              <g key={m.id} data-role="marker" data-at={m.at}>
                <line
                  x1={x}
                  x2={x}
                  y1={0}
                  y2={PLOT_H}
                  strokeWidth="1"
                  strokeDasharray="2 2"
                  className="stroke-ink-soft"
                />
                <text
                  x={x + 3}
                  y={PLOT_H + 10}
                  fontSize="8"
                  className="fill-ink-soft font-mono tabular-nums"
                >
                  {m.label}
                </text>
              </g>
            )
          })}
          {/* WHERE EACH SERIES ENDS, which is the figure the question was
              usually about, and the last mark this component was missing.
              Read off `plot`'s own points rather than re-derived, so a dot
              cannot land off the line it belongs to, and haloed in the card's
              own paper because a series usually ends at its high or its low
              and would otherwise merge with the rule carrying that label. */}
          {p.lines.map((line) => {
            const last = line.points[line.points.length - 1]
            if (last === undefined) return null
            return (
              <g key={line.name} data-role="chart-point">
                <circle cx={last.x} cy={last.y} r={4.5} className="fill-surface" />
                <circle cx={last.x} cy={last.y} r={2.75} className="fill-ink" />
              </g>
            )
          })}
        </g>
        {/* A figure without its unit is the defect `Figure` exists to
            prevent, and a value axis of bare digits carries no currency at
            all. Once, above the drawing, rather than on both marks -- the
            high and the low are ruled and named on the plot itself now, so
            this corner is free. */}
        {unit && (
          <text
            data-role="chart-unit"
            x={VIEW_W - PAD_R}
            y={PAD_T - 4}
            fontSize={AXIS_SIZE}
            textAnchor="end"
            className="fill-ink-soft"
          >
            {unit}
          </text>
        )}
      </svg>
      {/* THE TIME AXIS, and it was written once and only for the other
          renderer. `Projection.tsx`'s `series` block draws the window's ends
          under the drawing and argues why at length: "the question is usually
          'over the last month' and the drawing carried no time at all, so the
          horizontal direction meant nothing". Every word of that applies here,
          and this component — the one the catalog resolves `chart` to, which
          is what a NEPSE index history actually draws through — never got it.
          A reader asking for "last year" saw a shape with two values on it and
          nothing saying when it started.

          The labels are the wire's own `x` strings, read through `nptDate`
          where they parse as a date and left exactly as they arrived where
          they do not — a fiscal year is not a date, and `nptDate` abstaining
          is the signal to print what came. Same treatment as the sibling, so
          the two renderers cannot disagree about what a window's end reads
          like. */}
      {(firstX || lastX) && (
        <figcaption
          data-role="chart-window"
          className="flex items-baseline justify-between gap-2
                               font-mono text-mono-s tabular-nums text-ink-soft"
        >
          <span className="whitespace-nowrap">{axisLabel(firstX)}</span>
          <span className="whitespace-nowrap">{axisLabel(lastX)}</span>
        </figcaption>
      )}
      {caption && (
        <figcaption data-role="caption" className="text-body-xs text-ink-soft">
          {caption}
        </figcaption>
      )}
    </figure>
  )
}
